import React from 'react';
import {
  Grid,
  Typography,
  Popover,
  MenuItem,
  Avatar,
  MenuList,
  Paper,
  Box,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { DefaultUserIconFilled } from 'components/SVG';
import { useResourceShares } from 'hooks/share/useResourceShare';
import { useShareTypes } from 'hooks/share/useShareTypes';
import { useResourceTypes } from 'hooks/EntityTypes/useResourceTypes';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { useResourceAccessLevels } from 'hooks/share/useResourceAccessLevels';
import type { ResourceShare } from 'types/Share/ResourceShares';

type BoardCollaboratorsPopupProps = {
  resourceID: string;
};

function BoardCollaboratorsPopup({ resourceID }: BoardCollaboratorsPopupProps) {
  const isHoveringPopover = React.useRef(false);
  const isHoveringAvatars = React.useRef(false);
  const avatarsRef = React.useRef<HTMLDivElement | null>(null);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const { idByNameMap } = useShareTypes();
  const { idByKeyMap } = useResourceTypes();

  const resourceTypeID = idByKeyMap?.[SPARK_RESOURCE_TYPES.BOARDS] || '';

  const { resourceShares: allResourceShares, getResourceShareUser } =
    useResourceShares(resourceID, resourceTypeID);

  const {
    accessLevelDropdownOptions,
    getAccessLevelIDByResourceAccessLevelID,
  } = useResourceAccessLevels(resourceTypeID);

  const [collaborators] =
    allResourceShares?.reduce<ResourceShare[][]>(
      (acc, curr) => {
        if (!idByNameMap) return acc;
        if (curr.attributes.shareTypeID === +idByNameMap['Link']) {
          acc[1].push(curr);
          return acc;
        }
        acc[0].push(curr);
        return acc;
      },
      [[], []]
    ) || [];

  if (!collaborators?.length) {
    return (
      <Box display={'flex'} justifyContent={'center'}>
        <Avatar
          sx={{
            bgcolor: (theme) => theme.palette.primary.light,
            color: (theme) => theme.palette.primary.main,
            width: '40px',
            height: '40px'
          }}
        >
          <PersonIcon fontSize="small" />
        </Avatar>
      </Box>
    );
  }

  const accessLevelSharesInitialValues = collaborators?.reduce<
    Record<string, string>
  >((acc, curr) => {
    return {
      ...acc,
      [curr.id]:
        getAccessLevelIDByResourceAccessLevelID(
          curr.attributes.resourceAccessLevelID
        ) || '',
    };
  }, {});

  const updatePopupVisibility = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (isHoveringAvatars.current || isHoveringPopover.current) {
      if (avatarsRef.current && !anchorEl) {
        setAnchorEl(avatarsRef.current);
      }
    } else {
      timeoutRef.current = setTimeout(() => {
        setAnchorEl(null);
      }, 300);
    }
  };

  const handleAvatarsMouseEnter = () => {
    isHoveringAvatars.current = true;
    updatePopupVisibility();
  };

  const handleAvatarsMouseLeave = () => {
    isHoveringAvatars.current = false;
    updatePopupVisibility();
  };

  const handlePopoverMouseEnter = () => {
    isHoveringPopover.current = true;
    updatePopupVisibility();
  };

  const handlePopoverMouseLeave = () => {
    isHoveringPopover.current = false;
    updatePopupVisibility();
  };

  const getProfileImageURL = (userID: string | null) => {
    if (!userID) return;
    const user = getResourceShareUser(userID);
    return user?.attributes.profilePictureURL;
  };

  const getName = (userID: string | null, email?: string | null) => {
    if (!userID) return email;
    const user = getResourceShareUser(userID);
    return user?.attributes.name || email;
  };

  return (
    <>
      <Grid
        container
        justifyContent='flex-start'
        component='div'
        ref={avatarsRef}
        onMouseEnter={handleAvatarsMouseEnter}
        onMouseLeave={handleAvatarsMouseLeave}
        sx={{ cursor: 'pointer', width: 'fit-content' }}
      >
        {collaborators?.map((collaborator, index) => (
          <Grid 
            item 
            key={collaborator.id}
            sx={{ 
              marginLeft: index !== 0 ? '-8px' : '0px',
              zIndex: collaborators.length - index,
            }}
          >
            <Avatar
              alt={collaborator.attributes.createdAt}
              src={getProfileImageURL(collaborator.attributes.userID) || ''}
              sx={{
                bgcolor: (theme) => theme.palette.primary.light,
                color: (theme) => theme.palette.primary.main,
                border: '2px solid white',
              }}
            >
              <Typography variant='body2'>
                {getName(
                  collaborator.attributes.userID,
                  collaborator.attributes.email
                )
                  ?.slice(0, 2)
                  .toUpperCase()}
              </Typography>
            </Avatar>
          </Grid>
        ))}
      </Grid>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableRestoreFocus
        sx={{
          pointerEvents: 'none',
          '& .MuiPaper-root': {
            pointerEvents: 'auto',
          },
        }}
      >
        <Paper
          ref={popoverRef}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
          sx={{
            minWidth: '400px',
          }}
        >
          <MenuList>
            {collaborators?.map((collaborator) => {
              const roleId = accessLevelSharesInitialValues[collaborator.id];
              const roleName = accessLevelDropdownOptions.find(
                (role) => role.value === roleId
              )?.label;

              return (
                <MenuItem
                  key={collaborator.id}
                  disabled={true}
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    '&.Mui-disabled': {
                      opacity: 1,
                      color: 'text.primary'
                    }
                  }}
                >
                  <Box display={'flex'} alignItems={'center'}>
                    <Avatar
                      sx={{
                        bgcolor: (theme) => theme.palette.primary.light,
                        color: (theme) => theme.palette.primary.main,
                        marginRight: 1,
                      }}
                      src={
                        getProfileImageURL(collaborator.attributes.userID) || ''
                      }
                    >
                      <Typography variant="body2">
                        {getName(
                          collaborator.attributes.userID,
                          collaborator.attributes.email
                        )
                          ?.slice(0, 2)
                          .toUpperCase()}
                      </Typography>
                    </Avatar>
                    <Typography variant='body2'>
                      {getName(
                        collaborator.attributes.userID,
                        collaborator.attributes.email
                      )}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant='body2'>{roleName}</Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </MenuList>
        </Paper>
      </Popover>
    </>
  );
}

export default BoardCollaboratorsPopup;
